import * as THREE from 'three';
import { Object3D, Points, Raycaster, Vector2, Vector3 } from 'three';

import { isShownBelow } from '../../utils/three/visibility';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { SkyviewOrthographicCamera } from '../cameras/skyview-orthographic-camera';
import {
  getCameraScreenPosition,
  SkyviewPerspectiveCamera,
} from '../cameras/skyview-perspective-camera';
import { SkyviewCamera } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { isStyledLines, StyledLinesIntersection } from '../elements/styled-lines/styled-lines';
import { CellGridVolumeModel } from '../elements/volume/model/cell-grid-volume-model';
import { Tiled3DModelHandle } from '../geodata/3d-tiled-models/tiled-3d-model-handle';
import { getAttributePositionAtIndex } from '../geodata/point-clouds/cesium-point-cloud/points/buffer-attribute/position-buffer-attribute';
import { PointCloudHandle } from '../geodata/point-clouds/point-cloud-handle';
import { Ground } from '../ground';
import { Pickable } from '../pickable/pickable';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { excludeAsset, isAssetPickable } from '../tools/select-tool';
import { ViewSettings } from '../view-settings';
import { findPointOnLineFromHeight, getPerpendicularVector, toVector2 } from './math-helpers';

export interface PickingOptions {
  rayPointThreshold?: number;
}

export function setRayCasterParamsBasedOnCamera(
  camera:
    | SkyviewPerspectiveCamera
    | SkyviewOrthographicCamera
    | THREE.PerspectiveCamera
    | THREE.OrthographicCamera,
  rayCaster: Raycaster,
  pixelSnapDistance = 20,
  expectedCameraDistance = 30,
  width: number = 1,
  height: number = 1,
) {
  /**
   * Base the raycasters treshold on how many pixels 1 meter is represented by on the screen.
   * we set this value to slightly higher than the pixel snap distance to ensure we will
   * always successfully intersect against any objects within reach of the cursor.
   */
  const referencePoint1 = rayCaster.ray.origin
    .clone()
    .add(rayCaster.ray.direction.clone().multiplyScalar(expectedCameraDistance));

  const perpendicularVector = getPerpendicularVector(rayCaster.ray.direction);
  const referencePoint2 = referencePoint1.clone().add(perpendicularVector);

  const isSkyviewCamera =
    camera instanceof SkyviewPerspectiveCamera || camera instanceof SkyviewOrthographicCamera;

  const screenPos1 = isSkyviewCamera
    ? camera.getScreenPosition(referencePoint1)
    : getCameraScreenPosition(camera, referencePoint1, width, height);
  const screenPos2 = isSkyviewCamera
    ? camera.getScreenPosition(referencePoint2)
    : getCameraScreenPosition(camera, referencePoint2, width, height);

  const pixelsPerMeter = Math.max(1, screenPos1.distanceTo(screenPos2));

  rayCaster.params.Mesh!.threshold = (pixelSnapDistance * 0.8) / pixelsPerMeter;
  rayCaster.params.Line.threshold = (pixelSnapDistance * 1.2) / pixelsPerMeter;
  rayCaster.params.Points.threshold = (pixelSnapDistance * 1.22) / pixelsPerMeter;
}

export class Pick {
  private static _instance: Pick = new Pick();
  static get instance(): Pick {
    return this._instance;
  }

  private pointCloud?: Pickable;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  setActivePointCloud(pointCloud?: Pickable) {
    this.pointCloud = pointCloud;
  }

  /**
   * Picks the closest point in view, from assets, point cloud or tiled model, depending on what is available.
   */
  closestPoint(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    options?: {
      skipPointCloud?: boolean;
      skipAssets?: boolean;
      skipTiledModel?: boolean;
      /**
       * If defined raycast will fallback to picking from a height, where the ray reaches it in
       * front of the camera and within its view, see Pick.groundHeight.
       */
      pickHeight?: number;
      /**
       * Determines which assets that can be picked.
       * @default isAssetPickable
       */
      assetPredicate?: (asset: Object3D) => boolean;
    },
  ): Vector3 | undefined {
    const assetIntersection =
      options?.skipAssets === true
        ? undefined
        : this.closestAssetIntersection(camera, rayCaster, options?.assetPredicate);

    if (isDefined(assetIntersection)) {
      return this.getIntersectionPoint(assetIntersection);
    }

    // The front-most near the cursor, of the point cloud and the tiled model, which the flexible
    // viewing mode shows together. Only those shown: a hidden layer is still there to be hit.
    const geodataPoint = [
      options?.skipPointCloud === true || !isTimelineLayerPicked('pointCloud')
        ? undefined
        : PointCloudHandle.instance.pickNearCursor(camera, rayCaster),
      options?.skipTiledModel === true || !isTimelineLayerPicked('tiledModel')
        ? undefined
        : Tiled3DModelHandle.instance.pickNearCursor(camera, rayCaster),
    ].reduce<THREE.Intersection | undefined>(
      (frontMost, hit) =>
        hit && (!frontMost || hit.distance < frontMost.distance) ? hit : frontMost,
      undefined,
    )?.point;

    if (!isDefined(geodataPoint) && isDefined(options?.pickHeight)) {
      return this.pointOnHeight(camera, rayCaster, options.pickHeight);
    }

    return geodataPoint;
  }

  /**
   * Gets where the ray from the camera reaches the height, where the camera sees it: not behind
   * the camera, as a ray towards the sky would reach it, nor beyond its far plane. On the ray, the
   * point is within the view, so only its depth is checked, as the far plane is at a depth, not a
   * distance along a slanted ray. A point closer than the near plane, in front of the camera, is
   * still where the cursor is.
   */
  pointOnHeight(camera: SkyviewCamera, rayCaster: Raycaster, height: number) {
    const { origin, direction } = rayCaster.ray;
    const point = findPointOnLineFromHeight(origin, direction, height);
    if (!point || point.clone().sub(origin).dot(direction) < 0) {
      return undefined;
    }

    return point.clone().project(camera.getTHREECamera()).z <= 1 ? point : undefined;
  }

  /**
   * The height the camera's pivot falls back to where nothing is picked, see groundHeight. None
   * while a tiled model or point cloud is picked from: the pivot then keeps its depth, rather than
   * dropping through gaps in the model to the ground far behind it.
   */
  cameraFallbackHeight(): number | undefined {
    const pickedFromData =
      (isTimelineLayerPicked('tiledModel') &&
        !!Tiled3DModelHandle.instance.getActiveTiledModel()) ||
      (isTimelineLayerPicked('pointCloud') && !!PointCloudHandle.instance.activePointCloud);
    return pickedFromData ? undefined : this.groundHeight();
  }

  /**
   * The height in the scene of the ground: the orthophoto's, where it is shown, otherwise the
   * project's height 0. The tools don't fall back to it: their height snap sets the height.
   */
  groundHeight() {
    const settings = ViewSettings.instance;
    const orthophotoShown =
      settings.layers.orthophoto && !!ProjectGeodataManager.instance().getActivOrthotileGeodata();
    const height = orthophotoShown ? settings.orthophoto.altitude : 0;
    return height - (CoordinateManager.getInstance().getProjectionOrigo()?.z ?? 0);
  }

  /**
   * Gets the intersection with the assets that is closest to the ray on screen. With linePixels,
   * a line within that many pixels is preferred over closer surfaces, e.g. the outline of a filled
   * shape over its fill.
   */
  closestAssetIntersection(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    predicate: (asset: Object3D) => boolean = isAssetPickable,
    linePixels?: number,
  ): THREE.Intersection | undefined {
    const intersections = this.assetIntersections(camera, rayCaster, predicate);
    const line = isDefined(linePixels)
      ? intersections.find((x) => isLineIntersection(x) && x.distanceToRay! <= linePixels)
      : undefined;

    return line ?? intersections[0];
  }

  /**
   * Gets the intersections with the visible assets, closest to the ray on screen first, with the
   * distance from it in pixels as distanceToRay.
   */
  assetIntersections(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    predicate: (asset: Object3D) => boolean = isAssetPickable,
  ): THREE.Intersection[] {
    return this.assets(0, 0, predicate, {
      camera,
      rayCast: rayCaster,
      pixelSortScreenPosition: {
        client: toVector2(
          camera.getScreenPosition(rayCaster.ray.origin.clone().add(rayCaster.ray.direction)),
        ),
      },
      skipPrioritizePointSnapping: true,
    }).filter((intersection) => isShownBelow(intersection.object));
  }

  /**
   * Gets the position of an intersection, the position of the point itself for points.
   */
  getIntersectionPoint(intersection: THREE.Intersection): Vector3 {
    if (intersection.object instanceof THREE.Points && isDefined(intersection.index)) {
      return intersection.object.localToWorld(
        getAttributePositionAtIndex(intersection.object as Points, intersection.index),
      );
    }

    return intersection.point;
  }

  pointcloudPoint(
    x: number,
    y: number,
    options?: PickingOptions,
    rayCast?: Raycaster,
    points?: Points[],
  ): THREE.Vector3 | undefined {
    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return undefined;
    }
    let intersections;
    const ray = rayCast ?? camera.getRay(x, y);
    ray.params.Points.threshold = options?.rayPointThreshold || ray.params.Points?.threshold || 0;
    // A ray from the camera through the cursor picks like the other cursor picks, not from a hidden
    // point cloud. Other rays, e.g. along a line, pick from it all the same.
    const cursorPick = !rayCast && !points;
    if (
      this.pointCloud &&
      this.pointCloud.available() &&
      (!cursorPick || isTimelineLayerPicked('pointCloud'))
    ) {
      if (cursorPick) {
        return PointCloudHandle.instance.pickNearCursor(camera, ray)?.point;
      }
      intersections = this.pointCloud.pick(ray, points);
    } else {
      intersections = Ground.instance.pick(ray);
    }

    return intersections && intersections.length > 0 ? intersections[0].point : undefined;
  }

  height(x: number, y: number, height: number): THREE.Vector3 | undefined {
    const rayCast = cameraManager().getActiveCamera()?.getRay(x, y);

    return rayCast
      ? findPointOnLineFromHeight(rayCast.ray.origin, rayCast.ray.direction, height)
      : undefined;
  }

  objectPoint(rayCaster: Raycaster, objects: Object3D[]): THREE.Vector3 | undefined {
    let intersections = rayCaster.intersectObjects(objects, true);

    for (const i of intersections) {
      i.distanceToRay = rayCaster.ray.distanceToPoint(i.point);
    }

    intersections = intersections.sort((a, b) => {
      if (a.distanceToRay! < b.distanceToRay!) {
        return -1;
      } else {
        return 1;
      }
    });

    if (intersections.length > 0 && intersections[0].point) {
      // Special case for points.
      // Get the position from index instead of the picked position if the picked geometry is points.
      if (
        intersections[0]?.object instanceof THREE.Points &&
        intersections[0].index !== undefined
      ) {
        const actualPosition = intersections[0].object.localToWorld(
          getAttributePositionAtIndex(intersections[0].object as Points, intersections[0].index),
        );

        return actualPosition;
      }

      return intersections[0].point;
    }

    return undefined;
  }

  assetPoint(
    x: number,
    y: number,
    predicate: (asset: Object3D) => boolean,
    options?: {
      camera?: SkyviewCamera;
      rayCast?: Raycaster;
      pixelSortScreenPosition?: { client: Vector2 };
      skipPrioritizePointSnapping?: boolean;
    },
  ): THREE.Vector3 | undefined {
    const intersections = this.assets(x, y, predicate, {
      camera: options?.camera,
      rayCast: options?.rayCast,
      pixelSortScreenPosition: options?.pixelSortScreenPosition,
      skipPrioritizePointSnapping: options?.skipPrioritizePointSnapping,
    }).filter((intersection) => isShownBelow(intersection.object));

    if (intersections.length > 0 && intersections[0].point) {
      // Special case for points.
      // Get the position from index instead of the picked position if the picked geometry is points.
      if (
        intersections[0]?.object instanceof THREE.Points &&
        intersections[0].index !== undefined
      ) {
        const actualPosition = intersections[0].object.localToWorld(
          getAttributePositionAtIndex(intersections[0].object as Points, intersections[0].index),
        );

        return actualPosition;
      }

      return intersections[0].point;
    }

    return undefined;
  }

  /**
   * Picks a point on a volume and returns information about position and height
   */
  volumePoint(x: number, y: number, model: CellGridVolumeModel): THREE.Intersection | undefined {
    const camera = cameraManager().getActiveCamera()?.getTHREECamera();
    const rayCaster = new THREE.Raycaster();
    rayCaster.params.Points.threshold = 0.071; // Note: Value adjusted for 0.1 spacing between volume points for optimal picking

    if (!camera) {
      return undefined;
    }

    rayCaster.setFromCamera(new Vector2(x, y), camera);

    let intersections = rayCaster.intersectObjects([model], true);

    for (const i of intersections) {
      i.distanceToRay = rayCaster.ray.distanceToPoint(i.point);
    }

    intersections = intersections.sort((a, b) => {
      if (a.distanceToRay! < b.distanceToRay!) {
        return -1;
      } else {
        return 1;
      }
    });

    return intersections[0];
  }

  assets(
    x: number,
    y: number,
    predicate: (asset: Object3D) => boolean,
    options?: {
      camera?: SkyviewCamera;
      includeTemp?: boolean;
      threshold?: number;
      rayCast?: Raycaster;
      pixelSortScreenPosition?: { client: Vector2 };
      skipPrioritizePointSnapping?: boolean;
    },
  ): THREE.Intersection[] {
    const camera = options?.camera || cameraManager().getActiveCamera();

    if (!camera) {
      return [];
    }

    const assets = AssetManager.instance.children.filter(predicate);
    const rayCaster = options?.rayCast ?? new THREE.Raycaster();

    if (!options?.rayCast) {
      rayCaster.setFromCamera(new Vector2(x, y), camera.getTHREECamera());
    }

    if (options?.includeTemp) {
      assets.push(AssetManager.instance.tempObj);
    }

    if (options?.threshold) {
      rayCaster.params.Line = { threshold: options.threshold };
      rayCaster.params.Mesh = { threshold: options.threshold };
    }

    let intersections = rayCaster.intersectObjects(assets, true);
    for (const i of intersections) {
      if (options?.pixelSortScreenPosition) {
        if (i.object instanceof Points) {
          i.point = new Vector3(
            (i.object as Points).geometry.attributes.position.getX(i.index!),
            (i.object as Points).geometry.attributes.position.getY(i.index!),
            (i.object as Points).geometry.attributes.position.getZ(i.index!),
          ).add(i.object.getWorldPosition(i.object.position.clone()));
        }

        const a = camera.getScreenPosition(i.point);
        i.distanceToRay = a.distanceTo(options.pixelSortScreenPosition.client);

        // Gives a slight snapping priority to points
        if (options?.skipPrioritizePointSnapping !== true && i.object instanceof Points === false) {
          i.distanceToRay = (i.distanceToRay ?? 0) + 12;
        }
      } else {
        i.distanceToRay = rayCaster.ray.distanceToPoint(i.point);
      }
    }

    intersections = intersections.sort((a, b) => {
      // Base sorting on distance from camera if its a very small treshold or the distanceToRay are the same.
      if (
        a.distanceToRay === b.distanceToRay ||
        (a.distanceToRay! < 0.002 && b.distanceToRay! < 0.002)
      ) {
        return a.distance < b.distance ? -1 : 1;
      }

      if (a.distanceToRay! < b.distanceToRay!) {
        return -1;
      } else {
        return 1;
      }
    });

    return intersections;
  }

  /**
   * Finds the place where the ray intersects with the target geometry
   * @param caster the caster, will be excluded from the intersection.
   * @param start start point of the line.
   * @param end end point of the line.
   * @param target Determines what the ray will intersect with.
   * @param pointCloudChunks If provided the intersection will be done towards the chunks instead of the pointcloud in the scene.
   * Note that the chunks needs to be attached to the pointcloud intersected or to a parent object with the same position as the pointcloud
   * otherwise the raycast will not find the intersection correctly.
   */
  onRay(
    caster: Object3D,
    start: Vector3,
    end: Vector3,
    target: 'asset' | 'pointcloud' | 'tiledModel',
    pointCloudChunks?: Points[],
    assetPredicate: (asset: Object3D) => boolean = isAssetPickable,
  ) {
    const filter = excludeAsset(caster, assetPredicate);
    const getAdjustedPosition = (dir: Vector3) => {
      const rayCast = new Raycaster(start, dir);
      let intersection: Vector3 | undefined;

      if (target === 'asset') {
        rayCast.params.Line = { threshold: 0 };
        rayCast.params.Points = { threshold: 0 };
        rayCast.params.Mesh = { threshold: 0 };
        intersection = Pick.instance.assetPoint(0, 0, filter, { rayCast });
      } else if (target === 'pointcloud') {
        rayCast.params.Points = { threshold: 1 };
        intersection = Pick.instance.pointcloudPoint(0, 0, undefined, rayCast, pointCloudChunks);
      } else if (target === 'tiledModel') {
        intersection = Tiled3DModelHandle.instance.pick(rayCast)[0]?.point;
      }

      // Pick a point on the line that is the same z height as the intersected point.
      return intersection ? findPointOnLineFromHeight(start, dir, intersection.z) : undefined;
    };

    const direction = new Vector3().subVectors(end, start).normalize();
    const adjustedPosition = getAdjustedPosition(direction);

    // If no point was found we also check the opposite slope before returning.
    return adjustedPosition ?? getAdjustedPosition(direction.setZ(-direction.z));
  }
}

/**
 * Whether the timeline layer is picked: when shown, and the point cloud in every classic view,
 * which picks from it, as its scenes did.
 */
const isTimelineLayerPicked = (layer: 'pointCloud' | 'tiledModel') =>
  ViewSettings.instance.layers[layer] ||
  (layer === 'pointCloud' && !ViewSettings.instance.isFlexible);

/**
 * Whether the intersection is with a line, or a curve, of drawn lines.
 */
const isLineIntersection = (intersection: THREE.Intersection) =>
  intersection.object instanceof THREE.Line ||
  (isStyledLines(intersection.object) &&
    (intersection as StyledLinesIntersection).item.kind !== 'triangle');
